import { Box, Drawer, DrawerBody, DrawerContent, DrawerHeader, DrawerOverlay, Flex, Heading, Step, StepDescription, StepIcon, StepIndicator, StepNumber, Stepper, StepSeparator, StepStatus, StepTitle, Tab, TabList, TabPanel, TabPanels, Tabs } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { IconButton } from '@material-ui/core';
import { MdContentCopy } from 'react-icons/md';

import { entityBaseBodyRender, Table } from '@app/components/common/table';
import { isGroupRow } from '@app/components/common/table/utils';
import { ConfirmRemoveDialog, FieldView, Txt } from '@app/components/common';
import { USER_DATE_FORMAT } from '@app/utils';
import { Equipment } from '@app/api/models';
import { useQueryTable } from '@app/components/common/table/query-table';
import { Transaction, TransactionHistoryItem, TransactionInfo, TransactionPackage, TransactionPackageDetail } from '@app/api/models/transaction';
import { useAlert, useApi } from '@app/hooks';

import { TxTypeView } from './tx-type';
import { Status } from './status';
import { EquipmentsList } from './equip-list';
import { ActionsComponent } from './actions';

export const TxDrawer = ({ 
  selectedTx,
  onClose,
 }: {
  selectedTx?: TransactionInfo | TransactionPackage;
  onClose(): void;
}) => {
  const { refetch } = useQueryTable();
  const [tx, setTx] = useState<TransactionPackageDetail | Transaction>();
  const [actions, setActions] = useState<number[]>(null);
  const [statusHistory, setStatusHistory] = useState<TransactionHistoryItem[]>(null);
  const api = useApi();
  const { success } = useAlert();

  useEffect(() => {
    (async () => {
      if (!selectedTx) {return;}
      if (selectedTx.transactions_count) { 
        const tx = await api.transaction.getTransactionPackage({ package_id: '' + selectedTx.id });
        setTx(tx.data);
        const txFirst = await api.transaction.getTransaction(tx.data.transactions[0].id);

        setStatusHistory(txFirst.data.status_history);
        setActions(txFirst.data.actions);
        return; 
      }
      const tx = await api.transaction.getTransaction((selectedTx as TransactionInfo).id);
      setStatusHistory(tx.data.status_history);
      setActions(tx.data.actions);
      setTx({ ...tx.data, status: tx.data.status_history.at(-1)?.status });
    })();
    

  }, [selectedTx]);

  const header = useMemo(() => {
    if (!selectedTx) {return null;}
    if (isGroupRow<Transaction>(selectedTx)) {return `Пакет #${selectedTx.group}`;}
    return `Транзакция #${selectedTx.id}`;
  }, [selectedTx]);

  const isPackage = useMemo(() => {
    if (!selectedTx) {return false;}
    return !!selectedTx.transactions_count;
  }, [selectedTx]);

  const txType = useMemo(() => {
    if (!tx) {return null;}
    return tx.type;
  }, [tx]);

  const status = useMemo(() => {
    if (!tx) {return null;}
    return tx.status;
  }, [tx]);

  const from = useMemo(() => {
    if (!tx) {return null;}
    return tx.outlet_from;
  }, [tx]);

  const to = useMemo(() => {
    if (!tx) {return null;}
    return tx.outlet_to;
  }, [tx]);

  const distributor = useMemo(() => {
    if (!tx) {return null;}
    return tx.distributor;
  }, [tx]);

  const onRemove = useCallback((id) => () => {
    if (!isPackage) {return;}
  }, [isPackage]);

  const onChange = useCallback(async () => {
    await refetch();
  }, [refetch]);
  
  const canAction = useMemo(() => {
    if (!actions) {return false;}
    return actions.length > 0;
  }, [actions]);
  
  const onCopyEquips = useCallback(async () => {
    if (!tx) {return;}
    if (!tx.transactions) {
      if ((tx as Transaction).transaction_model) {
        await navigator.clipboard.writeText(
          `Модель\n${() => (tx as Transaction).transaction_model.name}`
        );
        success('Скопировано в буфер обмена');  
        return;
      }
      const {id, model, serial_num, inventory_num} = (tx as Transaction).equip;
      await navigator.clipboard.writeText(
        `ID;Модель;Серийный номер;Инвентарный номер\n${id};${model.name};${serial_num};${inventory_num}`
      );
      success('Скопировано в буфер обмена');
      return;
    }
    const equips = tx.transactions.map(({ equip }) => equip);
    await navigator.clipboard.writeText(
      'ID;Модель;Серийный номер;Инвентарный номер\n' +
      equips.map((item) => `${item.id};${item.model.name};${item.serial_num};${item.inventory_num}`).join('\n')
    );
    success('Скопировано в буфер обмена');
  }, [selectedTx]);

  if (!selectedTx) {return null;}

  return <>
    <Drawer placement='right' onClose={onClose} isOpen={!!selectedTx}>
      <DrawerOverlay />
      <DrawerContent minW={550}>
        <DrawerHeader borderBottomWidth='1px'>{header}</DrawerHeader>
        <DrawerBody display='flex' flexDirection='column' justifyContent='space-between' >
          <Box>
            <Box display='flex' justifyContent='space-between' mt={2} pb={4} >
              <TxTypeView type={txType} full size='md' />
              <Status status={status} size='md' />
            </Box>
            <Tabs mx={-6}>
              <TabList >
                <Tab>Данные</Tab>
                <Tab>История</Tab>
                {isPackage && <Tab>Пакет</Tab>}
              </TabList>
              <TabPanels>
                <TabPanel>
                  <Box>
                    <Heading backgroundColor='brand.400' color='white' display='flex' alignItems='center' justifyContent='space-between'  mx={-4} mb={4} px={4} size="sm" py={2}>
                      Холодильное оборудование
                      <IconButton onClick={onCopyEquips} aria-label="copy-equips">
                        <MdContentCopy color='white' />
                      </IconButton>
                    </Heading>
                    {isPackage && <EquipmentsList  
                      equips={tx?.transactions?.map(({ equip }) => equip ) || []}
                    />}
                    {!isPackage && <>
                      {!!(tx as Transaction)?.transaction_model && <FieldView 
                        title="Модель"
                        value={(tx as Transaction)?.transaction_model.name}
                        canCopy
                      />}
                      <Txt color='brand.500' >{(tx as Transaction)?.equip?.model?.name || (tx as Transaction)?.model?.name}</Txt>
                      {!!(tx as Transaction)?.equip && <><FieldView 
                        title="Инв.номер"
                        value={(tx as Transaction)?.equip.inventory_num}
                        canCopy
                      />
                      <FieldView 
                        title="Серийный номер"
                        value={(tx as Transaction)?.equip.serial_num}
                        canCopy
                      /></>}
                    </>}
                  </Box>
                  {!!from?.id && <Box>
                    <Heading backgroundColor='brand.400' color='white' mx={-4} my={4} px={4} size="sm" py={2}>ТТ отправки</Heading>
                    <Txt color='brand.500' >{from.title}</Txt>
                    <FieldView 
                      title="ID"
                      value={from.id}
                      canCopy
                    />
                    <FieldView 
                      title="Юр.наименование"
                      value={from.legal_name}
                      canCopy
                    />
                    <FieldView 
                      title="Адрес"
                      value={from?.full_address || from.address?.full_address}
                      canCopy
                    />
                  </Box>}
                  {!!to?.id && <Box>
                    <Heading backgroundColor='brand.400' color='white'  mx={-4} my={4} px={4} size="sm" py={2}>ТТ назначения</Heading>
                    <Txt color='brand.500' >{to.title}</Txt>
                    <FieldView 
                      title="ID"
                      value={to.id}
                      canCopy
                    />
                    <FieldView 
                      title="Юр.наименование"
                      value={to.legal_name}
                      canCopy
                    />
                    <FieldView 
                      title="Адрес"
                      value={to?.full_address || to.address?.full_address}
                      canCopy
                    />
                  </Box>}
                  {!!distributor?.id && <Box>
                    <Heading backgroundColor='brand.400' color='white'  mx={-4} my={4} px={4} size="sm" py={2}>Дистрибьютор</Heading>
                    <FieldView 
                      title="ID"
                      value={distributor.id}
                      canCopy
                    />
                    <FieldView 
                      title="Наименование"
                      value={distributor.name}
                      canCopy
                    />
                  </Box>}
                </TabPanel>
                <TabPanel>
                  {statusHistory && <Stepper index={statusHistory?.length - 1} orientation='vertical' height='400px' gap='0'>
                    {statusHistory.map((step, index) => (
                      <Step key={index} style={{ width: '100%' }}>
                        <StepIndicator>
                          <StepStatus
                            complete={<StepIcon />}
                            incomplete={<StepNumber />}
                            active={<StepNumber />}
                          />
                        </StepIndicator>

                        <Box flexShrink='0' w='calc(100% - 48px)'>
                          <StepTitle>{step.status.name}</StepTitle>
                          <StepDescription  >
                            <Box display='flex' justifyContent='space-between'>
                              <Txt>{moment(step.create_dt).format(USER_DATE_FORMAT)}</Txt>
                              <Txt color='brand.500' textAlign='right'>{step.user?.full_name}</Txt>
                            </Box>
                            <Box display='flex' justifyContent='space-between' mt={2}>
                              <FieldView title='Комментарий' value={step.comment}/>
                            </Box>
                          </StepDescription>
                        </Box>

                        <StepSeparator />
                      </Step>
                    ))}
                  </Stepper>}
                </TabPanel>

                {isPackage && <TabPanel>
                  <Box mx={-4}>
                    <Table<Equipment>
                      title={<Txt fontSize='medium'>Холодильное оборудование</Txt>}
                      columns={[
                        { name: 'id', label: '#', options: { filter: false } },
                        { name: 'model', label: 'Модель', options: { customBodyRender: entityBaseBodyRender() } },
                        { name: 'inventory_num', label: 'Инв.номер', options: { filter: false } },
                        { name: 'serial_num', label: 'Серийный номер', options: { filter: false } },
                        { name: 'id', label: '', options: { 
                            filter: false,
                            customBodyRender: (id) => <ConfirmRemoveDialog 
                              text='Исключить ХО из пакета?' 
                              buttonText='Исключить'
                              onRemove={onRemove(id)} 
                            />
                          }   
                        }
                      ]} 
                      data={tx?.transactions?.map(({ equip }) => equip ) || []}
                      paper={false}
                    />
                  </Box>
                </TabPanel>}
              </TabPanels>
            </Tabs>
          </Box>
          {canAction && <Box display='flex' justifyContent='space-between' pb={4}>
            <ActionsComponent 
              selectedTx={tx}
              packageId={isPackage ? selectedTx?.id : tx.package_id} 
              onChange={onChange}
              onAction={() => onClose()}
            />
          </Box>}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  </>;
};